import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './App.css';
import { details } from 'framer-motion/client';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Image Carousel Component
const ImageCarousel = ({ images }) => {
  const [[currentIndex, direction], setCurrentIndex] = useState([0, 0]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  // Auto-play functionality - change image every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const newIndex = (currentIndex + 1) % images.length;
      setCurrentIndex([newIndex, 1]);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  return (
    <div className="carousel-container">
      <div className="carousel-wrapper">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 150, damping: 40 },
              opacity: { duration: 0.4 }
            }}
            className="carousel-slide"
          >
            <img src={images[currentIndex]} alt={`Slide ${currentIndex + 1}`} />
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="image-frame"></div>
    </div>
  );
};

// Reusable animated section component
const AnimatedSection = ({ children, className, id }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Add to Calendar function
const addToCalendar = () => {
  const event = {
    title: 'Hanna & Axel Bryllup',
    start: '20260724T150000',
    end: '20260726T120000',
    location: 'Rømskog Spa & Resort, Vortungen, 1950 Rømskog, Norway',
    description: 'Bryllupsfeiringen til Hanna og Axel. Vi gleder oss til å se deg!'
  };

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start}/${event.end}&location=${encodeURIComponent(event.location)}&details=${encodeURIComponent(event.description)}`;

  window.open(googleCalendarUrl, '_blank');
};

function App() {
  const { scrollYProgress } = useScroll();
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [menuOpen, setMenuOpen] = useState(false);

  // Parallax effect for hero
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
    }
  };

  // Countdown to wedding date
  useEffect(() => {
    const weddingDate = new Date('2026-07-25T15:00:00'); // REPLACE WITH YOUR DATE

    const interval = setInterval(() => {
      const now = new Date();
      const difference = weddingDate - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App">
      {/* Navigation Menu */}
      <nav className="nav-menu">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => scrollToSection('hero')}>
            <span className="nav-names">H & A</span>
          </div>

          <button
            className={`menu-toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <li><button onClick={() => scrollToSection('hero')}>Hjem</button></li>
            <li><button onClick={() => scrollToSection('details')}>Detaljer</button></li>
            <li><button onClick={() => scrollToSection('schedule')}>Program</button></li>
            <li><button onClick={() => scrollToSection('practical-info')}>Praktisk Info</button></li>
            <li><button onClick={() => scrollToSection('toastmaster')}>Kaoskontrollen</button></li>
          </ul>
        </div>
      </nav>

      {/* Decorative Background Elements */}
      <div className="decorative-bg">
        <div className="botanical-shape botanical-1"></div>
        <div className="botanical-shape botanical-2"></div>
        <div className="botanical-shape botanical-3"></div>
      </div>

      {/* Hero Section */}
      <motion.section id="hero" className="hero" style={{ y: heroY }}>
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="hero-text"
          >
            <img
              src="/images/h_og_a_logo.png"
              alt="Hanna & Axel"
              className="hero-logo"
            />
            <p className="wedding-date">24-26 Juli, 2026</p>
            <p className="location-hint">Rømskog Spa & Resort</p>
          </motion.div>

          {/* Countdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="countdown"
          >
            <div className="countdown-item">
              <span className="countdown-number">{countdown.days}</span>
              <span className="countdown-label">Dager</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{countdown.hours}</span>
              <span className="countdown-label">Timer</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{countdown.minutes}</span>
              <span className="countdown-label">Minutter</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{countdown.seconds}</span>
              <span className="countdown-label">Sekunder</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="scroll-indicator"
          >
            <div className="scroll-chevrons">
              <span className="chevron"></span>
              <span className="chevron"></span>
            </div>
          </motion.div>
        </div>

      </motion.section>

      {/* Our Story Section */}
      <AnimatedSection id="story" className="story-section">
        <div className="section-content">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="story-grid"
          >
            <motion.div variants={fadeInUp} className="story-text">
              <h2 className="section-title">Du og Jeg</h2>
              <div className="story-content">
                <p className="story-paragraph">
                  Året er 2013. Det er vår i lufta, vi er 15 år gamle og aner lite om hva livet har i vente. 13 år senere, i 2026,  står vi her, klare for å "tie the knot". 
                  Med hele ungdomstiden bak oss – videregående, russetid, militærtjeneste og studier – står vi fortsatt her, hånd i hånd (og ja, det overrasket oss også).
                </p>
                <p className="story-paragraph">
                  Den 14. februar 2025, en helt unik og samtidig ekstremt forutsigbar dato, bestemte vi oss for å gjøre det offisielt. 
                  På selveste Valentine’s Day, lovte vi hverandre å holde sammen resten av livet. 
                  Originalt? Kanskje ikke. Riktig? Absolutt.
                </p>
                <p className="story-paragraph">
                  Nå gjør vi oss klare for livets neste kapittel, og sammen ønsker vi dere, venner og familie, 
                  hjertelig velkommen til feiringen av kjærligheten vår!
                </p>

              </div>
            </motion.div>

            {/* Story Image Carousel - REPLACE WITH YOUR PHOTOS */}
            <motion.div variants={fadeInUp} className="story-image">
              <ImageCarousel
                images={[
                  '/images/h_og_a_polaroid.jpg',
                  '/images/h_og_a_st_bryllup.jpg',
                  '/images/hvaler.jpg',
                  '/images/go_manunited.png',
                  '/images/statue.png'
                ]}
              />
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Wedding Details Section */}
      <AnimatedSection id="details" className="details-section">
        <div className="section-content">
          <h2 className="section-title centered">Feiringen</h2>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="details-grid"
          >
            <motion.div variants={fadeInUp} className="detail-card">
              <div className="detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <h3>Når</h3>
              <p className="detail-primary">Fredag - Søndag</p>
              <p className="detail-secondary">July 24-26, 2026</p>
              <button className="add-to-calendar-btn" onClick={addToCalendar}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"></path>
                </svg>
                Legg til i kalender
              </button>
            </motion.div>

            <motion.div variants={fadeInUp} className="detail-card">
              <div className="detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <h3>Hvor</h3>
              <p className="detail-primary">Rømskog Spa & Resort</p>
              <p className="detail-secondary">Adresse: Vortungen, 1950</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="detail-card">
              <div className="detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h3>Kleskode</h3>
              <p className="detail-primary">Fredag:</p>
              <p className="detail-secondary">Penklær</p>
              <p className="detail-primary">Lørdag:</p>
              <p className="detail-secondary">Mørk dress / Smoking</p>
              <p className="detail-secondary">Lang kjole</p>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Schedule Section */}
      <AnimatedSection id="schedule" className="schedule-section">
        <div className="section-content">
          <h2 className="section-title">Program</h2>
          <p class="toastmaster-name">Oppdateres fortløpende</p>

          <div className="timelines-container">
            {/* Friday Timeline */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="day-section"
            >
              <h3 className="day-title">Fredag, 24.Juli</h3>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="timeline"
              >
                {[
                  { time: '15:00', event: 'Innsjekk - Fredagsgjester' },
                  { time: '18:00', event: 'Felles middag', detail: 'Middag og mineralvann dekkes av brudeparet.' },
                  { time: '20:00', event: 'Sosialt', detail: 'Mingling, quiz og mulighet for å bruke spa fasiliteter.' },
                  { time: '00:00', event: 'Leggetid for brudgommen' }
                ].map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="timeline-item">
                    <div className="timeline-time">{item.time}</div>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <h4>{item.event}</h4>
                      {item.detail && <p>{item.detail}</p>}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Saturday Timeline */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="day-section"
            >
              <h3 className="day-title">Lørdag, 25.Juli</h3>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="timeline"
              >
                {[
                  { time: '08.00-10.30', event: 'Frokost', detail: '' },
                  { time: '14.00', event: 'Vielse i Setskog kirke', detail: 'Lørdagsgjester sjekker inn etter vielsen.' },
                  { time: '15:30', event: 'Apertiff og mingling', detail: '' },
                  { time: '17.00', event: 'Brudeskål og kake', detail: '' },
                  { time: '18.00', event: 'Middag', detail: '' },
                  { time: '22.00', event: 'Party Time', detail: 'Fri bar fra kl. 22-02' },
                  { time: '01.00', event: 'Nattmat', detail: '' }
                ].map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="timeline-item">
                    <div className="timeline-time">{item.time}</div>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <h4>{item.event}</h4>
                      <p>{item.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Sunday Timeline */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="day-section"
            >
              <h3 className="day-title">Søndag, 26.Juli</h3>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="timeline"
              >
                {[
                  { time: '08.00-10.30', event: 'Frokost', detail: '' },
                  { time: '12.00', event: 'Utsjekk', detail: 'Gjestene kan benytte seg av hotellets fasiliteter frem til kl. 15:00.' }
                ].map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="timeline-item">
                    <div className="timeline-time">{item.time}</div>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <h4>{item.event}</h4>
                      <p>{item.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Practical Information Section */}
      <AnimatedSection id="practical-info" className="practical-info-section">
        <div className="section-content">
          <h2 className="section-title centered">Praktisk Informasjon</h2>

          <motion.div
            className="practical-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Accommodation */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <h3>Overnatting</h3>
              <p class="practical-note">Venneligst gi beskjed om du skal overnatte innen svarfristen 1.april</p>
              <p>Hotellovernatting i forbindelse med feiringen er helt frivillig og dekkes av gjestene selv.
                Det er mulig å bo fra fredag–søndag eller lørdag–søndag. Betaling skjer ved innsjekk/utsjekk.</p>
                <p>Oppgitte priser gjelder per natt.</p>
              <ul>
                <li>Enkeltrom: 1995,-</li>
                <li>Dobbeltrom: 2495,-</li>
              </ul>
            </motion.div>

            {/* Transportation */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"></path>
                  <path d="M9 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
                  <path d="M15 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
                </svg>
              </div>
              <h3>Transport & Parkering</h3>
              <p>Rømskog Hotell:</p>
              <p>Fra Oslo: Ca. 1,5 time med bil via E6</p>
              <p>Fra Mysen: Ca. 1 time med bil via Riksvei 123</p>
              <p>OBS - Hotellet ligger et godt stykke inn i skogen. Hvis du blir usikker er det mest sannsynlig 10 min igjen.</p>
              <p>Parkering for hele oppholdet koster 150,-</p>
              <p></p>
              <p>Setskog kirke:
                <p>Det er god parkeringsplass ved kirken og det tar ca. 25 min med bil fra hotellet</p>
              </p>
            </motion.div>

            {/* Gifts */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 12v10H4V12"></path>
                  <path d="M22 7H2v5h20V7z"></path>
                  <path d="M12 22V7"></path>
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
                </svg>
              </div>
              <h3>Gaver</h3>
              <p>Deres tilstedeværelse er den beste gaven!</p>
              <p>Hvis dere ønsker å gi noe, setter vi pris på bidrag til bryllupsreisen.</p>
            </motion.div>

            {/* Ceremony */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="8.5" cy="12" r="5.5"></circle>
                  <circle cx="15.5" cy="12" r="5.5"></circle>
                </svg>
              </div>
              <h3>Vielsen</h3>
              <p>Seremonien finner sted i Setskog kirke.</p>
              <p>Dato: 25.Juli</p>
              <p>Klokkeslett: 14.00.</p>
              <p>Det tar ca. 20 min å kjøre fra hotellet</p>
              <p className="practical-note">.</p>
              <p className="practical-note">.</p>
            </motion.div>

            {/* Children */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4"></circle>
                  <path d="M12 14v8"></path>
                  <path d="M8 18h8"></path>
                </svg>
              </div>
              <h3>Barn</h3>
              <p>Vi er glade i  barn, men ønsker at dette blir en voksenfest.</p>
              <p>Vi håper dere forstår og kan nyte en kveld uten de minste.</p>
            </motion.div>

            {/* Allergies */}
            <motion.div variants={fadeInUp} className="practical-card">
              <div className="practical-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <h3>Allergier & Mat</h3>
              <p>Har du allergier eller spesielle kostholdsbehov?</p>
              <p>Vennligst gi oss beskjed når du svarer på invitasjonen.</p>
              <p className="practical-note"></p>
            </motion.div>
          </motion.div>

        </div>
      </AnimatedSection>

      {/* Toastmaster Section */}
      <AnimatedSection id="toastmaster" className="toastmaster-section">
        <div className="section-content">
          <h2 className="section-title centered" style={{marginBottom: '6rem'}}>Kaoskontrollen</h2>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="toastmaster-grid"
          >
            <motion.div variants={fadeInUp} className="toastmaster-header">
              <h3 className="toastmaster-title">Toastmaster</h3>
              <p className="toastmaster-name">Martin Ferdinand Andresen</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="toastmaster-image">
              <img
                src="/images/toastmaster.JPG"
                alt="Toastmaster"
              />
              <div className="image-frame"></div>
            </motion.div>

            <motion.div variants={fadeInUp} className="toastmaster-text">
              <div className="toastmaster-content">
                <p className="toastmaster-paragraph">
                  Martin er sprudlende, engasjert og den eneste personen i rommet som vet mer om nabolaget ditt enn du selv gjør. 
                  Som byplanlegger har han full oversikt, elsker struktur og tar med seg dette inn i rollen som toastmaster med både alvor og glimt i øyet. 
                  Med energi som fyller rommet og en trygg hånd på rattet, er han akkurat den personen vi vil ha til å styre oss gjennom kvelden.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Wedding Party Section */}
      <AnimatedSection id="wedding-party" className="wedding-party-section">
        <div className="section-content">
          <h2 className="section-title centered">Forlovere</h2>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="wedding-party-grid"
          >
            {/* Maid of Honor */}
            <motion.div variants={fadeInUp} className="party-member">
              <div className="party-member-image">
                <img
                  src="images/embla.jpeg"
                  alt="Maid of Honor"
                />
              </div>
              <div className="party-member-info">
                <h4 className="party-name">Embla Hilmersen</h4>
                <p className="party-description">
                  Hannas kjære forlover, Embla. Hun startet som en kollega på Ris skole i 2023, men det tok ikke lang tid før hun hadde fått en sentral plass i livet hennes. 
                  Embla omtales nå hun som teacher bestie, work-wife, og livets venninne som hun ikke visste hun skulle være så heldig å møte i 20-årene. Dette er ei hardtarbeidende, 
                  omtenksom og herlig direkte nordlending som gir deg plusspoeng hvis du heier på Arsenal. 
                </p>
              </div>
            </motion.div>

            {/* Best Man */}
            <motion.div variants={fadeInUp} className="party-member">
              <div className="party-member-image">
                <img
                  src="images/mathias.jpeg"
                  alt="Best Man"
                />
              </div>
              <div className="party-member-info">
                <h4 className="party-name">Mathias Hoem-Larsen</h4>
                <p className="party-description">
                  Axel ble kjent med Mathias allerede i førsteklasse på barneskolen, og siden den gang har de klart kunststykket å holde sammen i over 20 år. 
                  Mathias er en usedvanlig omgjengelig, snill og surrete fyr som har spilt en enorm rolle i Axels liv, både når det gjelder gode råd, dårlige ideer og alt imellom. 
                  At han står ved Axels side som forlover er helt riktig, og vi krysser fingrene for at dette vil gjelde de neste 20 årene også.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-names">Hanna & Axel</p>
          <p className="footer-date">24-26. juli 2026</p>
          <p className="footer-note">Vi gleder oss til å feire dagen sammen med dere.</p><br />
          <p className="footer-note">Ved eventuelle spørsmål, ta kontakt:</p>
          <p className="footer-note">Hanna: 95156809 // hanna.yuan.lind@hotmail.com</p>
          <p className="footer-note">Axel: 90107490 // agrefs97@gmail.com</p>
        </div>

        <div className="footer-map">
          <h3 className="footer-map-title">Finn veien</h3>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1984.5!2d11.8894!3d59.7489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4641a5c8c8c8c8c9%3A0x0!2sR%C3%B8mskog%20Spa%20%26%20Resort!5e0!3m2!1sen!2sno!4v1234567890"
            width="100%"
            height="250"
            style={{ border: 0, borderRadius: '12px' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Rømskog Spa & Resort"
          ></iframe>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Rømskog+Spa+Resort+Vortungen+1950+Rømskog+Norway"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-directions-btn"
          >
            Få veibeskrivelse
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
